<template>
  <v-app>
    <the-fan-left-bar v-if="!isHide() && isFan()"></the-fan-left-bar>
    <the-left-bar v-else-if="!isHide()"></the-left-bar>
    <!-- 토스트($notify) — 커스텀 body 슬롯으로 브랜드 토스트 렌더 (흰 카드 + 타입 아이콘) -->
    <notifications position="top right" :width="340" :duration="2500">
      <template slot="body" slot-scope="{ item, close }">
        <div class="vn-toast" :class="`vn-toast--${item.type || 'info'}`">
          <div class="vn-toast__icon">
            <v-icon size="17" color="#fff">{{ toastIcon(item.type) }}</v-icon>
          </div>
          <div class="vn-toast__body">
            <div v-if="item.title" class="vn-toast__title">{{ item.title }}</div>
            <div v-if="item.text" class="vn-toast__text">{{ item.text }}</div>
          </div>
          <button class="vn-toast__close" type="button" @click="close">
            <v-icon size="15" color="#b8b2c8">mdi-close</v-icon>
          </button>
        </div>
      </template>
    </notifications>
    <v-main>
      <router-view v-if="authReady" />
      <!-- <transition name="fade"> </transition> -->
    </v-main>

    <!-- 로그인 후 하루 한 번, 미출석 시 자동 표시 -->
    <attendance-dialog v-model="showAttendance" />
  </v-app>
</template>

<script>
import localforage from "localforage";

import SupabaseApi from "./api/SupabaseApi";
import SupabaseFanApi from "./api/SupabaseFanApi";
import { supabase } from "./utils/supabase";
import TheLeftBar from "./views/TheLeftBar/TheLeftBar.vue";
import TheFanLeftBar from "./views/TheLeftBar/TheFanLeftBar.vue";
import AttendanceDialog from "./components/AttendanceDialog.vue";
import SlackWebhook from "./domain/slack/SlackWebhook";
import CurrencyApi from "./api/CurrencyApi";
import { setLocale } from "./plugins/i18n";

import { LAB_ROUTES } from "./routes-extra";

const hideTargetPages = LAB_ROUTES.filter((route) => route._hideLeftMenu === true) //
  .map((route) => route.name);

export default {
  components: { TheLeftBar, TheFanLeftBar, AttendanceDialog },
  name: "App",
  data: () => ({ authReady: false, showAttendance: false }),
  watch: {
    // 브라우저 탭 타이틀 다국어 — 로케일 전환 시 즉시 반영.
    // 로케일 타이틀 + VUE_APP_TITLE의 환경 접미사 "(local)/(dev)" 유지.
    "$i18n.locale": {
      immediate: true,
      handler() {
        const envMatch = (process.env.VUE_APP_TITLE || "").match(/\s*(\([^)]*\))\s*$/);
        document.title = this.$t("brand.title") + (envMatch ? " " + envMatch[1] : "");
      },
    },
  },
  async created() {
    // i18n 로케일(저장값/브라우저 감지)을 Vuetify 컴포넌트 로케일과 부팅 시 1회 동기화
    this.$vuetify.lang.current = this.$i18n.locale;

    // 라이트/다크 테마 — localStorage 저장값 복원 (사이드바 토글로 변경)
    this.$vuetify.theme.dark = localStorage.getItem("theme/dark") === "true";

    this.initLocalDatabase();

    const result = await this.authenticate();
    const path = this.$route.path;
    // 로그인 필요한 경로(스트리머용). OBS용 /on-air·/on-air/dev 는 ?t= 토큰 기반이라 제외.
    const protectedPaths = [
      "/home",
      "/broadcast",
      "/my/broadcast",
      "/broadcast/setting",
      "/pet/setting",
      "/pet/training",
      "/my",
      "/my/pet/reaction",
      "/my/items",
      "/fan",
      "/fan/recommend",
      "/fan/pet-search",
      "/fan/reactions",
      "/temp/dev/notice",
      "/temp/dev/asset-store",
      "/user/setting",
      "/temp/backdoor/current-modeler",
    ];

    // 리다이렉트를 await → 보호 페이지가 잠깐도 마운트되지 않게 (ANON 시 controller created 에러 방지)
    try {
      if (result.status === "ONBOARDING") {
        // 계정은 있으나 온보딩 미완료 → 사용자설정(/user/new)으로. 펫 생성(/pet/new)은 허용(튕김 방지).
        if (path !== "/user/new" && path !== "/pet/new") await this.$router.replace("/user/new");
      } else if (result.status === "REGISTERED") {
        // 이미 로그인된 사용자가 로그인/루트에 있으면 홈으로
        if (path === "/login" || path === "/") await this.$router.replace("/home");
      } else if (result.status === "FAN") {
        // 팬: 로그인/루트에서 진입 시 팬 공간으로
        if (path === "/login" || path === "/") await this.$router.replace("/fan");
      } else {
        // ANON: 보호 경로면 로그인으로
        if (protectedPaths.includes(path)) await this.denyAccess();
      }
    } catch (e) {
      // NavigationDuplicated 등 라우터 내비게이션 예외는 무시
    }

    // 인증 복원(+_accessToken 세팅)과 라우팅 결정이 끝난 뒤에야 페이지를 렌더한다.
    // (그 전에 보호 페이지가 mount되면 _accessToken 미설정으로 UUID_NOT_FOUND 발생)
    this.authReady = true;

    // 로그인 사용자에 한해 출석 다이얼로그 (하루 한 번, 미출석 시)
    if (result.status === "REGISTERED") this.maybeShowAttendance();
  },
  methods: {
    // 하루 한 번(KST) + 오늘 미출석이면 출석 다이얼로그 자동 표시
    async maybeShowAttendance() {
      try {
        const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Seoul" });
        if (localStorage.getItem("attendance/shownDate") === today) return; // 오늘 이미 띄움
        localStorage.setItem("attendance/shownDate", today); // 하루 한 번 보장(여러 새로고침 방지)

        const { lastCheckDate } = await CurrencyApi.fetchAttendance();
        if (lastCheckDate === today) return; // 오늘 이미 출석 → 스킵

        this.showAttendance = true;
      } catch (e) {
        console.error("maybeShowAttendance failed", e);
      }
    },
    async initLocalDatabase() {
      try {
        localforage.config({
          // WebSQL은 최신 Chrome(119+)에서 제거됨 → IndexedDB 우선, localStorage 폴백
          driver: [localforage.INDEXEDDB, localforage.LOCALSTORAGE],

          name: "myApp",
          storeName: "virtupet", // Should be alphanumeric, with underscores.

          description: "some description",
        });
      } catch (error) {
        console.error(error);
      }
    },
    async authenticate() {
      // Restore identity from the Supabase Auth session (JWT) rather than a bare localStorage email.
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData.session;
      const email = session?.user?.email ?? null;

      // 관리자 여부 — JWT app_metadata.is_admin (admin 앱과 동일 게이트). DB 조회 없음.
      this.$store.commit("__setIsAdmin", session?.user?.app_metadata?.is_admin === true);

      if (!email) return { status: "ANON" };

      SupabaseApi.setAuthUserId(session.user.id); // user_account 키
      SlackWebhook.setUser(email);

      // 팬/스트리머 분기 — 로그인 탭이 저장한 "활성 역할"(localStorage, 영속)로 판정.
      //  한 사람이 둘 다일 수 있어(같은 디스코드) fan_account 존재만으로 판단하지 않고 로그인 의도가 우선.
      const activeRole = localStorage.getItem("auth/role");
      if (activeRole === "fan") {
        // 최초 1회만 프로비저닝(+토큰 리프레시로 is_fan 반영). 이후엔 라우팅만.
        if (!(await SupabaseFanApi.Account.fetchMine())) {
          await SupabaseFanApi.Account.ensure();
          await supabase.auth.refreshSession();
        }
        this.$store.commit("__setIsFan", true); // 팬 사이드바 분기
        return { status: "FAN", email };
      }
      this.$store.commit("__setIsFan", false); // 스트리머(기본): 팬 사이드바 해제

      // 활성 펫(선택 펫) 1회 로드 → 이후 모든 펫 조회가 선택 펫 기준 (미선택이면 첫 펫 폴백)
      const selectedPetId = await SupabaseApi.UserStatus.fetchSelectedPetId();
      SupabaseApi.setSelectedPetId(selectedPetId);
      this.$store.commit("__setSelectedPetId", selectedPetId); // Vuex 미러

      // 제어판 pub은 펫 단위(getVirtupetPubSocket(petId)) — 전역 부트스트랩 불필요.

      // user_account/onboarding 행은 auth.users 트리거(on_auth_user_created → handle_new_user)가 보장.
      // 클라이언트 provisioning 제거. (트리거가 정상이면 항상 행 존재) null이어도 방어적으로 진행.
      const account = (await SupabaseApi.UserAccount.whoAmI()) || {};

      // 온보딩 미완료면 동물 선택으로. ('신규 가입' 슬랙은 auth.users 트리거 handle_new_user에서 DB가 발송)
      const isComplete = await SupabaseApi.UserAccountOnboarding.fetchIsComplete();
      if (!isComplete) return { status: "ONBOARDING", email };

      // REGISTERED: 스토어 하이드레이트 (email은 위에서 이미 전역 세팅됨)
      const { name, image_url: userImageUrl, locale, country_code: countryCode } = account;
      this.$store.commit("__setUserName", name);
      this.$store.commit("__setUserImageUrl", userImageUrl);
      this.$store.commit("__setUserCountryCode", countryCode || null);

      // 계정에 저장된 UI 언어 적용 (localStorage 대체). 미설정이면 부팅 시 브라우저 감지값 유지.
      if (locale) {
        setLocale(locale);
        this.$vuetify.lang.current = locale;
      }

      // 하이드레이트는 best-effort: 일부 쿼리 실패가 로그인 라우팅 전체를 막지 않도록 보호
      try {
        const profile = await SupabaseApi.PetProfile.fetchProfile();
        if (profile) this.$store.commit("__setVirtupetName", profile.name);
        const license = await SupabaseApi.BillingPurchase.fetchLicense();
        this.$store.commit("__setExpiredAt", license.expiredAt);
      } catch (e) {
        console.error("authenticate: hydrate failed (non-fatal)", e);
      }

      // 인증 컨텍스트(authUserId/선택 펫)가 모두 세팅된 뒤 사이드바 요약(펫/재화) 재로드 트리거.
      // (TheLeftBar는 authReady와 무관하게 마운트되어 child가 인증보다 먼저 로드될 수 있음)
      this.$store.commit("__bumpPetRevision");
      this.$store.dispatch("loadCurrencyBalance");

      return { status: "REGISTERED", account };
    },
    denyAccess() {
      localStorage.removeItem("userEmail");

      alert(this.$t("auth.loginRequired"));
      // 호출부(created)에서 await 할 수 있도록 내비게이션 Promise 반환
      return this.$router.push({ name: "Login" });
    },
    // 토스트 타입별 아이콘 (vn-toast__icon 칩 안)
    toastIcon(type) {
      return (
        { success: "mdi-check", error: "mdi-close", warn: "mdi-exclamation", warning: "mdi-exclamation" }[type] ||
        "mdi-information-variant"
      );
    },
    // 팬 여부 — 사이드바(스트리머/팬) 분기용
    isFan() {
      return this.$store.state.isFan === true;
    },
    isHide() {
      // TODO: routes.js 설정에서 가져올것
      // hide side panel 이런식으로
      return (
        [
          "Login",
          "IsNew",
          "Onboarding",
          "PetNew",
          "Splash",
          "Sandbox",
          "OnAir",
          "OnAirDev",
          "Pricing",
          ...hideTargetPages,
        ].indexOf(this.$route.name) > -1
      );
    },
  },
};
</script>

<style lang="scss">
body {
  background-color: #f4f5fa; /* body tag에는 bg 안먹힘 */
}
/* SASS Loader 방식으로 변경하기 */
.theme--light.v-application {
  color: #57545d;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

/* ===== 토스트 ($notify / vue-notification 커스텀 body) ===== */
.vn-toast {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 6px 10px;
  padding: 12px;
  background: #ffffff;
  border: 1px solid #efeaf7;
  border-radius: 13px;
  box-shadow: 0 12px 30px rgba(34, 22, 60, 0.16);
}
.vn-toast__icon {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #7c4dff; /* info(기본) */
}
.vn-toast--success .vn-toast__icon {
  background: #2ea36f;
}
.vn-toast--error .vn-toast__icon {
  background: #e8475f;
}
.vn-toast--warn .vn-toast__icon,
.vn-toast--warning .vn-toast__icon {
  background: #f0a020;
}
.vn-toast__body {
  flex: 1 1 auto;
  min-width: 0;
  padding-top: 1px;
}
.vn-toast__title {
  font-size: 13.5px;
  font-weight: 700;
  color: #2b2440;
  line-height: 1.35;
}
.vn-toast__text {
  font-size: 12.5px;
  color: #6f659a;
  margin-top: 2px;
  line-height: 1.4;
  word-break: break-word;
}
.vn-toast__close {
  flex: 0 0 auto;
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  margin: -2px -2px 0 0;
  border-radius: 6px;
  line-height: 0;
}
.vn-toast__close:hover {
  background: #f3f0fa;
}
</style>
